import { useState, useEffect } from 'react'
import { useAuth } from '../lib/AuthContext'
import { useLang } from '../lib/LangContext'
import { supabase } from '../lib/supabase'

function genererPIN() {
  return Math.floor(1000 + Math.random() * 9000).toString()
}

export default function GestionPIN() {
  const { boutiqueId } = useAuth()
  const { lang } = useLang()
  const [pin, setPin] = useState('')
  const [codeBoutique, setCodeBoutique] = useState('')
  const [pinActif, setPinActif] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState(null)
  const [showPin, setShowPin] = useState(false)

  useEffect(() => { if (boutiqueId) loadPin() }, [boutiqueId])

  async function loadPin() {
    const { data } = await supabase
      .from('boutiques')
      .select('caissier_pin, pin_actif, boutique_code')
      .eq('id', boutiqueId)
      .single()
    setPin(data?.caissier_pin || '')
    setCodeBoutique(data?.boutique_code || '')
    setPinActif(data?.pin_actif || false)
    setLoading(false)
  }

  async function genererNouveauPIN() {
    setSaving(true); setMsg(null)
    const newPin = genererPIN()
    const { error } = await supabase
      .from('boutiques')
      .update({ caissier_pin: newPin, pin_actif: true })
      .eq('id', boutiqueId)
    setSaving(false)
    if (error) { setMsg({ type: 'error', text: error.message }); return }
    setPin(newPin)
    setPinActif(true)
    setShowPin(true)
    setMsg({ type: 'success', text: lang === 'fr' ? '✓ Nouveau PIN généré !' : '✓ New PIN generated!' })
  }

  async function revoquerPIN() {
    if (!window.confirm(lang === 'fr' ? 'Révoquer le PIN ?' : 'Revoke PIN?')) return
    setSaving(true)
    await supabase.from('boutiques').update({ caissier_pin: null, pin_actif: false }).eq('id', boutiqueId)
    setPin(''); setPinActif(false); setShowPin(false); setSaving(false)
    setMsg({ type: 'success', text: lang === 'fr' ? '✓ PIN révoqué.' : '✓ PIN revoked.' })
  }

  if (loading) return <div style={{ fontSize: 13, color: 'var(--muted)' }}>Chargement...</div>

  return (
    <div>
      <div style={{ fontFamily: 'Fraunces, serif', fontSize: 16, color: 'var(--charcoal)', marginBottom: 8 }}>
        🔑 {lang === 'fr' ? 'PIN caissier' : 'Cashier PIN'}
      </div>
      <div style={{ fontSize: 12, color: 'var(--muted)', lineHeight: 1.7, marginBottom: '1rem' }}>
        {lang === 'fr'
          ? 'Partagez le code boutique ET le PIN avec votre caissier. Les deux sont nécessaires pour se connecter.'
          : 'Share the shop code AND the PIN with your cashier. Both are required to log in.'}
      </div>

      {pinActif && pin ? (
        <div style={{ background: 'var(--sage-pale)', border: '1.5px solid var(--sage-light)', borderRadius: 'var(--radius-lg)', padding: '1.25rem', marginBottom: '1rem' }}>

          {/* CODE BOUTIQUE */}
          <div style={{ marginBottom: '1rem', textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: 'var(--sage-dark)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {lang === 'fr' ? '1. Code boutique (fixe)' : '1. Shop code (fixed)'}
            </div>
            <div style={{ fontFamily: 'Fraunces, serif', fontSize: 36, color: 'var(--charcoal)', letterSpacing: 10, background: 'var(--warm-white)', borderRadius: 'var(--radius-md)', padding: '10px', border: '1px solid var(--cream-dark)' }}>
              {codeBoutique || '----'}
            </div>
          </div>

          {/* PIN */}
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 11, color: 'var(--sage-dark)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {lang === 'fr' ? '2. Code PIN (changeable)' : '2. PIN code (changeable)'}
            </div>
            <div style={{ fontFamily: 'Fraunces, serif', fontSize: 36, color: 'var(--sage-dark)', letterSpacing: 10, background: 'var(--warm-white)', borderRadius: 'var(--radius-md)', padding: '10px', border: '1px solid var(--sage-light)' }}>
              {showPin ? pin : '• • • •'}
            </div>
            <button onClick={() => setShowPin(s => !s)} style={{ fontSize: 12, color: 'var(--sage)', background: 'none', border: 'none', cursor: 'pointer', marginTop: 8 }}>
              {showPin ? (lang === 'fr' ? '🙈 Masquer' : '🙈 Hide') : (lang === 'fr' ? '👁 Afficher' : '👁 Show')}
            </button>
          </div>

          {/* MESSAGE À ENVOYER */}
          <div style={{ marginTop: '1rem', background: 'white', borderRadius: 'var(--radius-md)', padding: '10px 12px', fontSize: 12, color: 'var(--charcoal)', lineHeight: 1.8, border: '1px solid var(--cream-dark)' }}>
            <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {lang === 'fr' ? '📋 Message à copier pour votre caissier :' : '📋 Message to copy for your cashier:'}
            </div>
            {lang === 'fr'
              ? `Bonjour ! Pour accéder à notre boutique sur StockO :\n• Code boutique : ${codeBoutique}\n• Code PIN : ${showPin ? pin : '****'}\nOuvre StockO → "Accès caissier (PIN)"`
              : `Hi! To access our shop on StockO:\n• Shop code: ${codeBoutique}\n• PIN code: ${showPin ? pin : '****'}\nOpen StockO → "Cashier access (PIN)"`}
          </div>
        </div>
      ) : (
        <div style={{ background: 'var(--cream)', border: '1px dashed var(--cream-dark)', borderRadius: 'var(--radius-lg)', padding: '1.25rem', marginBottom: '1rem', textAlign: 'center', color: 'var(--muted)', fontSize: 13 }}>
          {lang === 'fr' ? 'Aucun PIN actif — cliquez ci-dessous pour en générer un' : 'No active PIN — click below to generate one'}
        </div>
      )}

      {msg && (
        <div className={msg.type === 'success' ? 'success-msg' : 'error-msg'} style={{ marginBottom: 10 }}>
          {msg.text}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <button className="btn-primary" onClick={genererNouveauPIN} disabled={saving} style={{ flex: 2 }}>
          {saving ? '...' : (pinActif ? (lang === 'fr' ? '🔄 Changer le PIN' : '🔄 Change PIN') : (lang === 'fr' ? '+ Générer un PIN' : '+ Generate PIN'))}
        </button>
        {pinActif && (
          <button onClick={revoquerPIN} disabled={saving} style={{ flex: 1, padding: 12, borderRadius: 'var(--radius-md)', border: '1.5px solid #DFA0A0', background: 'var(--rose-pale)', color: 'var(--rose)', cursor: 'pointer', fontSize: 13, fontFamily: 'DM Sans, sans-serif' }}>
            {lang === 'fr' ? 'Révoquer' : 'Revoke'}
          </button>
        )}
      </div>
    </div>
  )
}
